import React, { useState, useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Menu, Button, Spin, Empty, Alert, Input } from 'antd';
import { PlusOutlined, ReloadOutlined, SearchOutlined, LinkOutlined } from '@ant-design/icons';
import { 
  fetchOnlineWebsites, 
  retryFetchOnlineWebsites,
  resetOnlineWebsitesError,
  fetchCategories
} from '../../redux/slices/onlineWebsitesSlice';
import './OnlineAddComponent.css';
import FontIcon from '../common/FontIcon';
import AppIcon from '../AppIcon';
import { GlobalMessageApiContext } from '../../App';
import languageManager from '../../utils/languageManager';

const OnlineAddComponent = ({ onClose, onSave, onSaveAndContinue, initialData }) => {
  const dispatch = useDispatch();
  const messageApi = useContext(GlobalMessageApiContext);
  const { 
    websites, 
    loading, 
    error, 
    currentCategory,
    categories
  } = useSelector(state => state.onlineWebsites);
  
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchText, setSearchText] = useState('');

  // 首次加载时请求分类
  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  // 监听网站添加失败事件
  useEffect(() => {
    const handleWebsiteAddFailed = (event) => {
      const { website, reason } = event.detail;
      // 只有在非批量添加时才显示单个失败提示
      if (!website.batchId) {
        // 使用destroy()清除之前的消息，然后显示错误
        messageApi.destroy();
        messageApi.error(languageManager.t('onlineAddComponent.addFailed', { name: website.name, reason }));
      }
    };

    window.addEventListener('websiteAddFailed', handleWebsiteAddFailed);

    return () => {
      window.removeEventListener('websiteAddFailed', handleWebsiteAddFailed);
    };
  }, [messageApi]);

  // 当分类数据加载完成或currentCategory变化时，设置选中分类
  useEffect(() => {
    if (currentCategory && !selectedCategory) {
      setSelectedCategory(currentCategory);
    } else if (!selectedCategory && categories.length > 0) {
      // 跳过第一个分类（通常是"全部分类"），选择第二个分类作为默认
      const defaultCategoryIndex = categories.length > 1 ? 1 : 0;
      setSelectedCategory(categories[defaultCategoryIndex].id);
    }
  }, [currentCategory, categories, selectedCategory]);

  // 当选中分类变化时加载对应数据
  useEffect(() => {
    if (selectedCategory) {
      dispatch(fetchOnlineWebsites(selectedCategory));
    }
  }, [dispatch, selectedCategory]);

  // 处理分类切换
  const handleCategoryChange = (e) => {
    const category = e.key || e.id;
    setSelectedCategory(category);
    setSearchText(''); // 清空搜索
    dispatch(fetchOnlineWebsites(category));
  };

  // 处理添加所有网站
  const handleAddAllWebsites = () => {
    const filteredWebsites = getFilteredWebsites();
    
    if (filteredWebsites.length === 0) {
      messageApi.warning(languageManager.t('onlineAddComponent.noSitesToAdd'));
      return;
    }

    try {
      // 创建批量添加的跟踪状态
      const batchId = `batch_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
      const batchWebsites = new Map(); // 使用Map来跟踪每个网站的状态
      
      // 先显示开始批量添加的提示
      messageApi.loading(languageManager.t('onlineAddComponent.batchAdding', { count: filteredWebsites.length }));
      
      filteredWebsites.forEach(website => {
        if (onSaveAndContinue) {
          const websiteWithId = {
            ...website,
            id: website.id || `online_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`,
            iconUrl: website.ico,
            batchId: batchId // 添加批次ID用于跟踪
          };
          
          onSaveAndContinue(websiteWithId);
          batchWebsites.set(websiteWithId.id, { website: websiteWithId, status: 'pending' });
        }
      });
      
      // 监听失败事件，更新网站状态
      const handleBatchWebsiteFailed = (event) => {
        const { website } = event.detail;
        if (website.batchId === batchId && batchWebsites.has(website.id)) {
          batchWebsites.set(website.id, { website, status: 'failed' });
        }
      };
      
      // 监听成功事件，更新网站状态
      const handleBatchWebsiteSuccess = (event) => {
        const { website } = event.detail;
        if (website.batchId === batchId && batchWebsites.has(website.id)) {
          batchWebsites.set(website.id, { website, status: 'success' });
        }
      };
      
      // 添加临时事件监听器
      window.addEventListener('websiteAddFailed', handleBatchWebsiteFailed);
      window.addEventListener('websiteAddSuccess', handleBatchWebsiteSuccess);
      
      // 设置定时器来检查批量操作完成情况
      const checkBatchCompletion = () => {
        const pendingCount = Array.from(batchWebsites.values()).filter(item => item.status === 'pending').length;
        
        if (pendingCount === 0) {
          // 所有网站都已处理完成
          const successCount = Array.from(batchWebsites.values()).filter(item => item.status !== 'failed').length;
          const failedCount = Array.from(batchWebsites.values()).filter(item => item.status === 'failed').length;
          
          messageApi.destroy();
          
          if (failedCount === 0) {
            messageApi.success(languageManager.t('onlineAddComponent.batchAddSuccess', { count: successCount }));
          } else if (successCount === 0) {
            messageApi.error(languageManager.t('onlineAddComponent.batchAddAllFailed', { count: failedCount }));
          } else {
            messageApi.warning(languageManager.t('onlineAddComponent.batchAddPartial', { success: successCount, failed: failedCount }));
          }
          
          // 清理事件监听器
          window.removeEventListener('websiteAddFailed', handleBatchWebsiteFailed);
          window.removeEventListener('websiteAddSuccess', handleBatchWebsiteSuccess);
        } else {
          // 继续等待
          setTimeout(checkBatchCompletion, 500);
        }
      };
      
      // 开始检查完成情况
      setTimeout(checkBatchCompletion, 1000);
      
      // 设置超时清理
      setTimeout(() => {
        window.removeEventListener('websiteAddFailed', handleBatchWebsiteFailed);
        window.removeEventListener('websiteAddSuccess', handleBatchWebsiteSuccess);
      }, 10000); // 10秒后清理
      
    } catch (error) {
      messageApi.error(languageManager.t('onlineAddComponent.batchAddFailedRetry'));
      console.error('批量添加网站失败:', error);
    }
  };

  // 处理搜索
  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  // 处理添加网站
  const handleAddWebsite = (website) => {
    try {
      if (onSaveAndContinue) {
        onSaveAndContinue({
          ...website,
          id: website.id || `online_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`,
          iconUrl: website.ico,
        });
        // 只有在非批量添加时才显示成功提示
        if (!website.batchId) {
          messageApi.success(languageManager.t('onlineAddComponent.websiteAdded', { name: website.name }));
        }
      } else {
        messageApi.error(languageManager.t('onlineAddComponent.notConfigured'));
      }
    } catch (error) {
      messageApi.error(languageManager.t('onlineAddComponent.addFailedRetry'));
      console.error('添加网站失败:', error);
    }
  };

  // 处理重试
  const handleRetry = () => {
    dispatch(resetOnlineWebsitesError());
    dispatch(retryFetchOnlineWebsites(selectedCategory));
  };

  // 过滤数据（移除分页）
  const getFilteredWebsites = () => {
    if (!websites || websites.length === 0) return [];

    // 搜索过滤
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase();
      return websites.filter(website => 
        website.name.toLowerCase().includes(searchLower) ||
        website.description.toLowerCase().includes(searchLower)
      );
    }

    return websites;
  };

  // 渲染网站卡片
  const renderWebsiteCard = (item) => (
    <div key={item.id} className="online-add-card">
      <div className="online-add-card-content">
        <div className="online-add-card-icon">
          <AppIcon
            ico={item.ico}
            iconUrl={item.iconUrl}
            textIcon={item.textIcon || item.name?.substring(0, 4)}
            bgColor={item.bgColor || '#1890ff'}
            size={40}
            preferredIconType={item.preferredIconType}
          />
        </div>
        <div className="online-add-card-info">
          <h4 className="online-add-card-title">{item.name}</h4>
          <p className="online-add-card-description">{item.description}</p>
        </div>
      </div>
      <div className="online-add-card-actions">
        <a 
          href={item.url} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="online-add-card-url"
          onClick={(e) => e.stopPropagation()}
        >
          <LinkOutlined />
          {item.url}
        </a>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            handleAddWebsite(item);
          }}
          className="online-add-card-button"
        >
          {languageManager.t('common.add')}
        </Button>
      </div>
    </div>
  );

  // 渲染右侧内容
  const renderContent = () => {
    if (loading) {
      return (
        <div className="online-add-status">
          <Spin size="large" />
          <div style={{ marginTop: 16, color: 'white' }}>{languageManager.t('common.loading')}</div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="online-add-status">
          <Alert
            message={languageManager.t('onlineAddComponent.loadFailed')}
            description={error}
            type="error"
            showIcon
            action={
              <Button 
                size="small" 
                icon={<ReloadOutlined />}
                onClick={handleRetry}
              >
                {languageManager.t('common.retry')}
              </Button>
            }
          />
        </div>
      );
    }

    const filteredWebsites = getFilteredWebsites();

    if (filteredWebsites.length === 0) {
      return (
        <div className="online-add-status">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {searchText ? languageManager.t('onlineAddComponent.noResults') : languageManager.t('onlineAddComponent.noData')}
              </span>
            }
          />
        </div>
      );
    }

    return (
      <div className="online-add-grid">
        {filteredWebsites.map(renderWebsiteCard)}
      </div>
    );
  };

  const filteredWebsites = getFilteredWebsites();
  const selectedCategoryLabel = categories.find(cat => cat.id === selectedCategory)?.text || languageManager.t('onlineAddComponent.selectCategory');

  return (
    <div className="tab-content">
      <div className="online-add-component">
        <div className="online-add-sidebar">
          <Menu
            mode="vertical"
            selectedKeys={[selectedCategory]}
            onClick={handleCategoryChange}
            className="online-add-menu"
          >
            {categories.map(category => (
              <Menu.Item key={category.id} className="online-add-menu-item">
                <span className="category-icon"><FontIcon name={category.icon} /></span>
                <span className="category-label">{category.text}</span>
              </Menu.Item>
            ))}
          </Menu>
        </div>
        
        <div className="online-add-content">
          <div className="online-add-header">
            <Input
              placeholder={languageManager.t('onlineAddComponent.quickSearch')}
              allowClear
              value={searchText}
              onChange={handleSearchChange}
              className="online-add-search"
              suffix={<SearchOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddAllWebsites}
              className="online-add-all-button"
              disabled={loading || getFilteredWebsites().length === 0}
            >
              {languageManager.t('onlineAddComponent.addAll')}
            </Button>
          </div>
          
          <div className="online-add-content-body">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnlineAddComponent; 